import { create } from "zustand";

// Light/dark theme. Default is "system" (follow the OS); the toggle sets an
// explicit light/dark that persists. The active mode is applied as data-theme on
// <html> (theme.css swaps the CSS variables). index.html sets data-theme pre-paint
// from the same localStorage key to avoid a flash — keep the key in sync.

export type ThemePref = "system" | "light" | "dark";
export type ThemeMode = "light" | "dark";

const KEY = "calibre_theme";
const mql = () => window.matchMedia("(prefers-color-scheme: dark)");
const systemMode = (): ThemeMode => (mql().matches ? "dark" : "light");
const resolve = (p: ThemePref): ThemeMode => (p === "system" ? systemMode() : p);
const apply = (m: ThemeMode) => document.documentElement.setAttribute("data-theme", m);

interface ThemeState {
  pref: ThemePref;
  mode: ThemeMode;
  setPref: (p: ThemePref) => void;
  /** Flip between light and dark (sets an explicit preference). */
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const stored = ((): ThemePref => {
    try { const v = localStorage.getItem(KEY); return v === "light" || v === "dark" || v === "system" ? v : "system"; }
    catch { return "system"; }
  })();
  const mode = resolve(stored);
  apply(mode);

  // Follow the OS while the preference is "system".
  try {
    mql().addEventListener("change", () => {
      if (get().pref === "system") { const m = systemMode(); apply(m); set({ mode: m }); }
    });
  } catch { /* older browsers: no live updates, fine */ }

  const setPref = (p: ThemePref) => {
    try { localStorage.setItem(KEY, p); } catch { /* ignore */ }
    const m = resolve(p);
    apply(m);
    set({ pref: p, mode: m });
  };

  return {
    pref: stored,
    mode,
    setPref,
    toggle: () => setPref(get().mode === "dark" ? "light" : "dark"),
  };
});
